import React from "react";
import { motion } from "framer-motion";
import { ASSETS, EMAIL } from "../lib/constants";
import { PILLARS } from "../data/content";
import { CountUp } from "./CountUp";

export default function CaseyStory() {
  return (
    <section id="story" className="relative py-24 md:py-32 px-6 md:px-10 border-t border-white/5" data-testid="story-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="md:col-span-5 relative"
        >
          <div className="shape-r overflow-hidden relative" style={{ background: "radial-gradient(120% 90% at 50% 20%, rgba(var(--glow),0.22), transparent 65%)" }}>
            <img src={ASSETS.portrait} alt="Casey Muze" className="w-full max-h-[80vh] object-contain object-bottom" data-testid="casey-portrait" />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="glass shape-r p-6" data-testid="metric-events">
              <div className="font-serif text-4xl md:text-5xl text-primary-c"><CountUp end={450} suffix="+" testid="metric-events-count" /></div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 mt-2">Drumming Events</div>
            </div>
            <div className="glass shape-r p-6" data-testid="metric-lives">
              <div className="font-serif text-4xl md:text-5xl text-primary-c"><CountUp end={15000} suffix="+" testid="metric-lives-count" /></div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 mt-2">Lives Touched</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="md:col-span-6 md:col-start-7"
        >
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary-c mb-4">Who is Casey Muze</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.05]">Standing in the gap for the creative soul.</h2>
          <blockquote className="mt-6 font-serif italic text-xl md:text-2xl text-fg/80 leading-relaxed border-l-2 border-primary-c pl-5">
            "God created me to audaciously stand in the gap for the creative soul — right in the middle of mental health."
          </blockquote>

          <div className="mt-10 space-y-6">
            {PILLARS.map((p) => (
              <div key={p.title} className="flex gap-5">
                <div className="font-mono text-primary-c text-sm mt-1">—</div>
                <div>
                  <h4 className="font-medium text-lg">{p.title}</h4>
                  <p className="text-fg/60 leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10" data-testid="casey-credentials">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-fg/45 mb-3">Credentials &amp; Recognition</p>
            <div className="flex flex-wrap gap-2.5">
              <a
                href="https://traplearning.org/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="credential-trap"
                className="shape-r border border-primary-c/50 bg-primary-c/10 text-primary-c font-mono text-[10px] tracking-[0.12em] uppercase px-3.5 py-2 hover:bg-primary-c/20 transition-colors"
              >
                Certified TRAP Provider ↗
              </a>
              {["Amazon Best-Selling Author", "Region 7 ESC Education Partner", "Founder · AvenueSpeak LLC", "East Texas 40 Under 40 (2023)"].map((c) => (
                <span key={c} className="shape-r border border-white/15 text-fg/70 font-mono text-[10px] tracking-[0.12em] uppercase px-3.5 py-2">{c}</span>
              ))}
            </div>
          </div>

          <p className="mt-10 text-fg/70 leading-relaxed md:text-lg">
            In a church hall, a young boy found the drum as a turning point — gently shifting rhythm from casual play into a source of healing. Two decades later, Casey channels that pivot into drumming therapy, rhythmic sound sessions, and restorative keynotes: gentle lifelines for those weathering their own storms. <span className="text-fg">When words fall short, rhythm speaks.</span>
          </p>

          <a href={`mailto:${EMAIL}`} data-testid="story-email-btn" className="inline-block mt-8 shape-r border border-white/25 px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-white/10 transition-colors">
            Email Casey
          </a>
        </motion.div>
      </div>
    </section>
  );
}
